import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { HttpEventType } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Subscription } from 'rxjs';
import { CheckPageAccess } from 'src/app/components/authorize/check-page-access';
import { RoleOnboardingAcknowledgement, RoleOnboardingCourse, ROLE_ONBOARDING_VIDEO_MATERIAL_TYPE } from 'src/app/components/role-onboarding/role-onboarding';
import { AuthService } from 'src/app/services/auth.service';
import { FileService } from 'src/app/services/file.service';
import { RoleOnboardingService } from 'src/app/services/role-onboarding.service';
import { ToastService } from 'src/app/services/toast-service.service';

@Component({
    selector: 'app-view-role-onboarding',
    templateUrl: './view-role-onboarding.component.html',
    styleUrls: ['./view-role-onboarding.component.scss'],
    standalone: false
})
export class ViewRoleOnboardingComponent implements OnInit, OnDestroy {
  course: RoleOnboardingCourse | null = null;
  acknowledgements: RoleOnboardingAcknowledgement[] = [];
  columnsToDisplay = ['User', 'Completed', 'VideoUrl'];
  pageName = CheckPageAccess.ROLEONBOARDING;
  showEditButton = this.authService.getAccessControl().roleOnboardingManagement;
  courseId = 0;
  isPageLoading = true;
  coursePlayableUrl = '';
  roleCourses: RoleOnboardingCourse[] = [];

  showAddForm = false;
  newTitle = '';
  newDescription = '';
  newVideoUrl = '';
  newIsMandatory = true;
  newVideoSource: 'link' | 'upload' = 'link';
  newVideoFileId: number | null = null;
  uploadedFileName = '';
  isUploading = false;
  uploadProgress = 0;
  addSubmitted = false;
  isSaving = false;

  private routeSub?: Subscription;
  private urlResolveSubs: Subscription[] = [];

  constructor(
    private roleOnboardingService: RoleOnboardingService,
    private fileService: FileService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private toast: ToastService) { }

  ngOnInit(): void {
    this.routeSub = this.route.params.subscribe(params => {
      this.courseId = +params['id'];
      this.loadPage();
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.urlResolveSubs.forEach(sub => sub.unsubscribe());
  }

  openAdd(): void {
    this.newTitle = '';
    this.newDescription = '';
    this.newVideoUrl = '';
    this.newIsMandatory = true;
    this.newVideoSource = 'link';
    this.newVideoFileId = null;
    this.uploadedFileName = '';
    this.uploadProgress = 0;
    this.addSubmitted = false;
    this.showAddForm = true;
  }

  closeAdd(): void {
    if (!this.isSaving && !this.isUploading) {
      this.showAddForm = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeAdd();
  }

  onVideoSourceChange(source: 'link' | 'upload'): void {
    this.newVideoSource = source;
  }

  onVideoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    this.isUploading = true;
    this.uploadProgress = 0;
    this.uploadedFileName = file.name;
    this.newVideoFileId = null;

    this.fileService.uploadMaterialFile(formData, ROLE_ONBOARDING_VIDEO_MATERIAL_TYPE).subscribe({
      next: (httpEvent) => {
        if (httpEvent.type === HttpEventType.UploadProgress && httpEvent.total) {
          this.uploadProgress = Math.min(99, Math.round((100 * httpEvent.loaded) / httpEvent.total));
        } else if (httpEvent.type === HttpEventType.Response) {
          this.newVideoFileId = httpEvent.body.id;
          this.uploadedFileName = httpEvent.body.fileName || file.name;
          this.uploadProgress = 100;
          this.isUploading = false;
        }
      },
      error: () => {
        this.isUploading = false;
        this.uploadProgress = 0;
        this.uploadedFileName = '';
        this.toast.showErrorMessage('Video upload failed. Please try again.');
        input.value = '';
      }
    });
  }

  get videoInvalid(): boolean {
    return this.newVideoSource === 'upload' ? !this.newVideoFileId : this.videoUrlInvalid;
  }

  get videoUrlInvalid(): boolean {
    const url = this.newVideoUrl.trim();
    if (!url) {
      return true;
    }
    try {
      return new URL(url).protocol !== 'https:';
    } catch {
      return true;
    }
  }

  saveAdd(): void {
    this.addSubmitted = true;
    if (!this.course || !this.newTitle.trim() || this.videoInvalid || this.isSaving || this.isUploading) {
      return;
    }

    this.isSaving = true;
    this.roleOnboardingService.create({
      role: this.course.role,
      title: this.newTitle.trim(),
      description: this.newDescription.trim(),
      videoUrl: this.newVideoSource === 'link' ? this.newVideoUrl.trim() : '',
      videoFileId: this.newVideoSource === 'upload' ? this.newVideoFileId : null,
      isActive: true,
      isMandatory: this.newIsMandatory,
      displayOrder: this.roleCourses.length + 1
    }).subscribe({
      next: (created) => {
        this.isSaving = false;
        if (created) {
          this.toast.showMessage('Training added successfully');
          this.showAddForm = false;
          this.loadRoleCourses();
        } else {
          this.toast.showErrorMessage('Unable to add training');
        }
      },
      error: () => {
        this.isSaving = false;
        this.toast.showErrorMessage('Unable to add training');
      }
    });
  }

  private loadRoleCourses(): void {
    if (!this.course) {
      return;
    }
    const role = this.course.role;
    this.roleOnboardingService.getAll().subscribe({
      next: (all) => {
        this.roleCourses = (all ?? [])
          .filter(c => c.role === role)
          .sort((a, b) => (Math.max(1, a.displayOrder) - Math.max(1, b.displayOrder)) || (a.id - b.id));
      }
    });
  }

  navigate(): void {
    this.router.navigate(['/edit-role-training/' + this.courseId]);
  }

  getVideoDisplayName(course: RoleOnboardingCourse): string {
    if (course.videoFileName) {
      return course.videoFileName;
    }

    if (course.videoUrl) {
      return this.getExternalVideoDisplayName(course.videoUrl);
    }

    if (course.videoFileId) {
      return `Uploaded video #${course.videoFileId}`;
    }

    return '';
  }

  getAckVideoDisplayName(row: RoleOnboardingAcknowledgement): string {
    if (row.videoDisplayName) {
      return row.videoDisplayName;
    }

    return this.getSnapshotDisplayName(row.videoUrlSnapshot);
  }

  hasCourseVideo(course: RoleOnboardingCourse): boolean {
    return !!(course.videoUrl || course.videoFileId || course.videoFileName);
  }

  hasAckVideo(row: RoleOnboardingAcknowledgement): boolean {
    return !!this.getAckVideoDisplayName(row);
  }

  openVideo(url: string | null | undefined): void {
    if (!url) {
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private loadPage(): void {
    this.isPageLoading = true;
    this.course = null;
    this.acknowledgements = [];
    this.coursePlayableUrl = '';
    this.urlResolveSubs.forEach(sub => sub.unsubscribe());
    this.urlResolveSubs = [];

    forkJoin({
      course: this.roleOnboardingService.getById(this.courseId),
      acknowledgements: this.roleOnboardingService.getAcknowledgements(this.courseId)
    }).subscribe({
      next: ({ course, acknowledgements }) => {
        this.course = course;
        this.acknowledgements = acknowledgements ?? [];
        this.isPageLoading = false;
        this.resolveVideoUrlsInBackground();
        this.loadRoleCourses();
      },
      error: () => {
        this.isPageLoading = false;
      }
    });
  }

  private resolveVideoUrlsInBackground(): void {
    if (this.course) {
      if (this.course.playableVideoUrl) {
        this.coursePlayableUrl = this.course.playableVideoUrl;
      } else if (this.course.videoUrl) {
        this.coursePlayableUrl = this.course.videoUrl;
      } else if (this.course.videoFileId) {
        this.resolveFileUrl(this.course.videoFileId, url => {
          this.coursePlayableUrl = url;
        });
      }
    }

    for (const row of this.acknowledgements) {
      const externalUrl = this.getSnapshotExternalUrl(row.videoUrlSnapshot);
      if (externalUrl) {
        row.playableVideoUrl = externalUrl;
        continue;
      }

      const fileId = this.getSnapshotFileId(row.videoUrlSnapshot);
      if (fileId) {
        this.resolveFileUrl(fileId, url => {
          row.playableVideoUrl = url;
        });
      }
    }
  }

  private resolveFileUrl(fileId: number, onResolved: (url: string) => void): void {
    const sub = this.fileService.downloadMaterialFile(fileId).subscribe({
      next: (response) => {
        if (response?.url) {
          onResolved(response.url);
        }
      }
    });
    this.urlResolveSubs.push(sub);
  }

  private getSnapshotDisplayName(snapshot: string | null | undefined): string {
    if (!snapshot) {
      return '';
    }

    const externalUrl = this.getSnapshotExternalUrl(snapshot);
    if (externalUrl) {
      return this.getExternalVideoDisplayName(externalUrl);
    }

    const fileId = this.getSnapshotFileId(snapshot);
    if (!fileId) {
      return '';
    }

    const parts = snapshot.split(':', 3);
    if (parts.length >= 3 && parts[2]) {
      return parts[2];
    }

    return `Uploaded video #${fileId}`;
  }

  private getSnapshotExternalUrl(snapshot: string): string | null {
    try {
      const url = new URL(snapshot);
      if (url.protocol === 'http:' || url.protocol === 'https:') {
        return snapshot;
      }
    } catch {
      // not an absolute URL
    }

    return null;
  }

  private getSnapshotFileId(snapshot: string): number | null {
    if (!snapshot.toLowerCase().startsWith('file:')) {
      return null;
    }

    const parts = snapshot.split(':', 3);
    if (parts.length < 2) {
      return null;
    }

    const fileId = Number(parts[1]);
    return Number.isFinite(fileId) && fileId > 0 ? fileId : null;
  }

  private getExternalVideoDisplayName(videoUrl: string): string {
    try {
      const url = new URL(videoUrl);
      const host = url.hostname.replace(/^www\./, '').toLowerCase();
      if (host.includes('youtube') || host === 'youtu.be') {
        return 'YouTube video';
      }
      if (host.includes('vimeo')) {
        return 'Vimeo video';
      }
      return host;
    } catch {
      return 'Video link';
    }
  }
}
